'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faBookmark } from '@fortawesome/pro-solid-svg-icons';
import { SingleBar } from '../SingleBar';

interface CodeSet {
  code_set: {
    id: number;
    label: string;
    type: string;
    scope: string;
  };
  claims: string;
  patients: string;
}

interface TreatingVolumeBarProps {
  codeSet: CodeSet;
  bgColor: string;
  fillColor: string;
  globalMax: number;
}

// Check if value is masked (e.g., "< 11", "<11", "< 11*")
const isMaskedValue = (value: string): boolean => {
  if (typeof value !== 'string') return false;
  return /^<\s*\d+/.test(value.trim());
};

const TreatingVolumeBar: React.FC<TreatingVolumeBarProps> = ({
  codeSet,
  bgColor,
  fillColor,
  globalMax,
}) => {
  const patientsRaw = codeSet.patients;
  const claimsRaw = codeSet.claims;

  const patientsMasked = isMaskedValue(patientsRaw);
  const claimsMasked = isMaskedValue(claimsRaw);

  // Parse numeric values, default to 0 for masked values
  const patients = patientsMasked ? 0 : parseInt(patientsRaw, 10);
  const claims = claimsMasked ? 0 : parseInt(claimsRaw, 10);

  const hasMaskedValue = patientsMasked || claimsMasked;
  const isZeroState = patients === 0 && claims === 0 && !hasMaskedValue;

  // Display values - use superscript for masked asterisk
  const maskedDisplay = (
    <>
      &lt;11<sup>*</sup>
    </>
  );
  const patientsDisplay = patientsMasked
    ? maskedDisplay
    : patients.toLocaleString();
  const claimsDisplay = claimsMasked ? maskedDisplay : claims.toLocaleString();

  // Plain text for aria labels
  const patientsAriaDisplay = patientsMasked
    ? '<11*'
    : patients.toLocaleString();
  const claimsAriaDisplay = claimsMasked ? '<11*' : claims.toLocaleString();

  // Zero state: simplified display with gray badge (only for true zero values)
  if (isZeroState) {
    return (
      <div
        className="flex flex-col gap-1 w-full"
        role="listitem"
        aria-label={`${codeSet.code_set.label}: 0 claims, 0 patients`}
        data-testid={`treating-volume-bar-${codeSet.code_set.id}`}
      >
        <div className="h-8 flex justify-start items-center gap-2 w-full">
          <div className="h-full px-4 bg-neutral-200 rounded-full flex items-center gap-1">
            <Icon
              icon={faBookmark}
              className="w-2.5 h-3 text-neutral-400 text-xs"
              aria-hidden="true"
            />
            <span className="text-neutral-400 text-sm font-medium">
              {codeSet.code_set.label}
            </span>
          </div>
          <span className="text-primary text-sm font-bold">0</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 w-full"
      role="listitem"
      aria-label={`${codeSet.code_set.label}: ${claimsAriaDisplay} claims, ${patientsAriaDisplay} patients`}
      data-testid={`treating-volume-bar-${codeSet.code_set.id}`}
    >
      {/* Label above */}
      <div className="flex items-center">
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
          <Icon
            icon={faBookmark}
            className="w-2.5 h-3 text-primary text-xs"
            aria-hidden="true"
          />
        </div>
        <div className="text-right text-primary text-sm font-bold font-['Inter'] leading-5">
          {codeSet.code_set.label}
        </div>
      </div>

      {/* Bars */}
      <div className="flex flex-col gap-1 w-full">
        {/* Claims bar */}
        <SingleBar
          label="Claims"
          value={claims}
          displayValue={claimsDisplay}
          ariaDisplayValue={claimsAriaDisplay}
          maxValue={globalMax}
          color={bgColor}
          isMasked={claimsMasked}
        />
        {/* Patients bar */}
        <SingleBar
          label="Patients"
          value={patients}
          displayValue={patientsDisplay}
          ariaDisplayValue={patientsAriaDisplay}
          maxValue={globalMax}
          color={fillColor}
          isMasked={patientsMasked}
        />
      </div>
    </div>
  );
};

export default TreatingVolumeBar;
